import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';

import { msalInstance } from '@/services/msal';
import { installThemeBridge } from '@/services/theme';
import { AuthProvider } from '@/contexts/AuthContext';
import { ApiClientProvider } from '@/contexts/ApiClientContext';
import { ConfigProvider } from '@/contexts/ConfigContext';
import { SessionProvider } from '@/contexts/SessionContext';
import { TelemetryProvider } from '@/contexts/TelemetryContext';
// BACKLOG-1297 — hosts the in-app confirm/prompt/alert dialog. Native browser
// dialogs are silently suppressed by the Teams webview, so every guard the app
// used to raise with window.confirm() now goes through this provider.
import { ConfirmProvider } from '@/components/primitives';
import { App } from '@/App';
import { APP_VERSION, APP_BUILD_TIME } from '@/constants/version';

// Install the Teams appearance-mode bridge BEFORE React paints the first
// frame (theme comes only from the Teams context, Bill 2026-11-03).
// `installThemeBridge` paints the Teams default
// (light) synchronously, then applies the Teams context theme and subscribes to Teams theme
// changes async — fire-and-forget.
void installThemeBridge();

import '@/styles/tokens.css';
import '@/styles/globals.css';
import '@/styles/layout.css';
import '@/styles/primitives.css';

// FlatIcon UIcons — banker-formal interface icon family for chrome /
// affordance / nav. Three families imported: regular-rounded (default
// outline), solid-rounded (filled hover/active), thin-rounded (decorative
// inline / large display). Bundled via npm, no CDN. See
// @/components/primitives/Icon.tsx for the React wrapper. Bill 2026-05-13.
import '@flaticon/flaticon-uicons/css/regular/rounded.css';
import '@flaticon/flaticon-uicons/css/solid/rounded.css';
import '@flaticon/flaticon-uicons/css/thin/rounded.css';
import { installSupportCapture } from '@retail-ops/support-report';
import { urlHasAuthResponse, runMsalRedirectBridge } from '@/services/msalRedirectBridge';

// "Report a problem": start the background console / error / network buffers before the first screen.
installSupportCapture();

// Hidden diagnostic — log version when ?v is present in URL.
if (typeof window !== 'undefined' && window.location.search.includes('v')) {
  // eslint-disable-next-line no-console
  console.info(`[PCB Retail] Version ${APP_VERSION} — built ${APP_BUILD_TIME}`);
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root missing from index.html');

const startApp = (): void => {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <ApiClientProvider>
            <ConfigProvider>
              <SessionProvider>
                <TelemetryProvider>
                  <ConfirmProvider>
                    <App />
                  </ConfirmProvider>
                  {/* REQ-82 keyless-cutover verification chip — always visible,
                      independent of App's auth gates. Removed once bearer-only ships. */}
        {/* AuthDiagBadge removed 2026-09-11 (Bill): it was the REQ-82 keyless-cutover
            diagnostic, by its own charter temporary until bearer validated. It has -
            bearerValid:true in production - so it is retired. Auth method now shows
            in Help -> About / the user menu. */}
                </TelemetryProvider>
              </SessionProvider>
            </ConfigProvider>
          </ApiClientProvider>
        </AuthProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
};

// MSAL v5: when this page load is the sign-in return (popup, hidden iframe or full-page redirect),
// hand the response to MSAL instead of starting the app here. See msalRedirectBridge.ts.
if (urlHasAuthResponse()) {
  void runMsalRedirectBridge().then((handled) => {
    if (!handled) startApp();
  });
} else {
  startApp();
}
